import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type ColumnFiltersState,
  type Header,
  type HeaderGroup,
  type PaginationState,
  type Row,
  type SortingState
} from '@tanstack/react-table';
import DOMPurify from 'dompurify';
import {
  Calendar,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronsLeft,
  ChevronsRight,
  ChevronsUpDown,
  Download,
  Filter,
  Pencil, Trash,
  X
} from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { Expense } from '../../../types/expenseTypes';
import { formatDisplayDate } from '../../../utils/dateFormatters';
import {
  DATE_RANGE_PRESETS,
  applyExpenseFilters,
  generateFilterOptions,
  isFilterActive,
  loadDefaultFilterPreferences,
  saveDefaultFilterPreferences
} from '../../../utils/filterUtils';
import { shortEmail } from '../../../utils/shortEmail';
import QuickActionButtons from '../buttons/QuickActionButtons';
import AuditTooltip from '../tooltip/AuditTooltip';
import ExpenseCard from './ExpenseCard';
import { Button } from '../../../ui';
import Modal from '../molecules/Modal';

interface EnterpriseDataTableProps {
  data: Expense[];
  canEdit?: (row: Expense) => boolean;
  canDelete?: (row: Expense) => boolean;
  isAdmin?: boolean;
  userRoles?: string[];
  assignedUnits?: string[];
  onEdit?: (row: Expense) => void;
  onDelete?: (row: Expense) => void;
  onApprove?: (row: Expense) => void;
  onReject?: (row: Expense) => void;
  onBulkApprove?: (rows: Expense[]) => void;
  onBulkReject?: (rows: Expense[]) => void;
  onDuplicate?: (row: Expense) => void;
  onExport?: (selectedData: Expense[], includeFields: string[]) => void;
  loading?: boolean;
  rowLoading?: Record<string, boolean>;
  tableClassName?: string;
  columnWidths?: Record<string, string>;
  columnClassNames?: Record<string, string>;
  containerClassName?: string;
  stickyPagination?: boolean; // Optional prop to enable sticky pagination
  fullData?: Expense[]; // Full dataset for generating filter options
  masterData?: {
    types?: Array<{ id: string; name: string }>;
    units?: Array<{ id: string; name: string }>;
    costCenters?: Array<{ id: string; name: string }>;
    categories?: Array<{ id: string; name: string }>;
  };
}

// Role-based edit permissions (days from creation)
const EDIT_PERMISSIONS = {
  user: 5,
  editor: 5,
  approver: 15,
  admin: 365, // ⚠️ TEMPORARY: Changed from 45 to 365 days for data migration corrections
  'super-admin': 365,
} as const;

const EnterpriseDataTable: React.FC<EnterpriseDataTableProps> = ({
  data,
  canEdit = () => false,
  canDelete = () => false,
  isAdmin = false,
  userRoles = [],
  assignedUnits = [],
  onEdit,
  onDelete,
  onApprove,
  onReject,
  onBulkApprove,
  onBulkReject,
  onDuplicate,
  onExport,
  loading = false,
  rowLoading = {},
  tableClassName,
  columnWidths,
  columnClassNames = {},
  containerClassName = '',
  stickyPagination,
  fullData,
  masterData,
}) => {
  // Filter data based on user access (must be at the top)
  const accessFilteredData = useMemo(() => {
    if (isAdmin) return data;
    return data.filter(expense => assignedUnits.includes(expense.unitId));
  }, [data, isAdmin, assignedUnits]);

  const [sorting, setSorting] = useState<SortingState>([
    { id: 'date', desc: true }, // Default: newest first by date
  ]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 25, // Reduced back to 25 for proper pagination display
  });
  const [showExportModal, setShowExportModal] = useState(false);
  const [selectedExportFields, setSelectedExportFields] = useState<string[]>([
    'date', 'type', 'unit', 'costCenter', 'category', 'nature', 'shop', 'amount'
  ]);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  
  // Toggle row expansion
  const toggleRowExpansion = (rowId: string) => {
    setExpandedRows(prev => ({
      ...prev,
      [rowId]: !prev[rowId]
    }));
  };
  
  // Set default filters based on user context and saved preferences
  const getDefaultFilters = () => {
    // First check for saved user preferences
    const savedPreferences = loadDefaultFilterPreferences();
    if (savedPreferences) {
      return {
        type: savedPreferences.type || '',
        unit: savedPreferences.unit || '',
        approvalStatus: savedPreferences.approvalStatus || '',
        dateFrom: savedPreferences.dateRange.start || '',
        dateTo: savedPreferences.dateRange.end || '',
        nature: '' // Add nature field
      };
    }
    
    // Fall back to role-based defaults
    const today = new Date();
    const sixMonthsAgo = new Date(today);
    sixMonthsAgo.setMonth(today.getMonth() - 6);
    
    // Default to last 6 months instead of 30 days for more data
    const defaultDateFrom = sixMonthsAgo.toISOString().split('T')[0];
    const defaultDateTo = today.toISOString().split('T')[0];
    
    // Default approval status - show all data by default instead of filtering
    let defaultApprovalStatus = '';
    // Removed automatic filtering by approval status to show more data
    // Users can manually filter if needed
    
    return {
      type: '',
      unit: '',
      approvalStatus: defaultApprovalStatus,
      dateFrom: defaultDateFrom,
      dateTo: defaultDateTo,
      nature: '' // Add nature field
    };
  };
  
  const [advancedFilters, setAdvancedFilters] = useState(getDefaultFilters);

  // Apply default column filters immediately on component mount
  React.useEffect(() => {
    const defaults = getDefaultFilters();
    
    // Set default approval status filter if applicable
    if (defaults.approvalStatus) {
      table.getColumn('approvalStatus')?.setFilterValue(defaults.approvalStatus);
    }
    
    // Set default date range filters
    if (defaults.dateFrom && defaults.dateTo) {
      // Apply date filtering through advanced filters since column filters don't handle date ranges
      setAdvancedFilters(defaults);
    }
    
    // For assigned units, set default unit filter if user has limited access
    if (!isAdmin && assignedUnits.length > 0 && assignedUnits.length <= 3) {
      // If user has few assigned units, default to first one for better UX
      const defaultUnit = assignedUnits[0];
      if (defaultUnit) {
        table.getColumn('unit')?.setFilterValue(defaultUnit);
      }
    }
  }, [userRoles, assignedUnits, isAdmin]);

  // Generate filter options from full dataset (not access-filtered)
  const filterOptions = useMemo(() => generateFilterOptions(fullData || data, masterData), [fullData || data, masterData]);

  // Apply advanced filters and default date range filtering
  const advancedFilteredData = useMemo(() => {
    let filteredData = accessFilteredData;
    
    // Always apply default date range filtering (unless advanced filters override)
    const defaults = getDefaultFilters();
    if (defaults.dateFrom && defaults.dateTo) {
      filteredData = applyExpenseFilters(filteredData, {
        dateRange: {
          start: defaults.dateFrom,
          end: defaults.dateTo
        }
      }, masterData);
    }
    
    // Apply advanced filters only when shown
    if (showAdvancedFilters) {
      filteredData = applyExpenseFilters(filteredData, {
        type: advancedFilters.type || undefined,
        units: advancedFilters.unit ? [advancedFilters.unit] : undefined,
        nature: advancedFilters.nature || undefined,
        approvalStatus: advancedFilters.approvalStatus as 'pending' | 'approved' | 'rejected' | 'all' | undefined,
        dateRange: advancedFilters.dateFrom && advancedFilters.dateTo ? {
          start: advancedFilters.dateFrom,
          end: advancedFilters.dateTo
        } : undefined
      }, masterData);
    }
    
    return filteredData;
  }, [accessFilteredData, showAdvancedFilters, advancedFilters, masterData]);

  // Selection logic
  const [selectedRowIds, setSelectedRowIds] = useState<Record<string, boolean>>({});
  
  const toggleRowSelected = (id: string) => {
    setSelectedRowIds(prev => {
      const newState = { ...prev };
      if (newState[id]) {
        delete newState[id]; // Remove completely when unchecked
      } else {
        newState[id] = true; // Add when checked
      }
      return newState;
    });
  };

  // Compute selected rows (only truly selected, approvable ones)
  const selectedRows = useMemo(() => {
    return accessFilteredData.filter(row => selectedRowIds[row.id] === true);
  }, [accessFilteredData, selectedRowIds]);

  // Count only pending expenses that can be approved
  const approvableCount = useMemo(() => {
    return selectedRows.filter(row => row.approvalStatus !== 'approved').length;
  }, [selectedRows]);

  // Count only non-rejected expenses that can be rejected
  const rejectableCount = useMemo(() => {
    return selectedRows.filter(row => row.approvalStatus !== 'rejected').length;
  }, [selectedRows]);

  // Helper function to resolve unit name from ID
  const resolveUnitName = (unitValue: string | undefined, masterData?: any) => {
    if (!unitValue || !masterData?.units) {
      return unitValue || '';
    }

    // First try to find by ID
    const unitById = masterData.units.find((u: any) => u.id === unitValue);
    if (unitById) {
      return unitById.name;
    }

    // Then try to find by name
    const unitByName = masterData.units.find((u: any) => u.name === unitValue);
    if (unitByName) {
      return unitByName.name;
    }

    // Fallback to original value
    return unitValue;
  };

  // Helper function to resolve cost center name from ID
  const resolveCostCenterName = (costCenterValue: string | undefined, masterData?: any) => {
    if (!costCenterValue || !masterData?.costCenters) {
      return costCenterValue || '';
    }

    // First try to find by ID
    const costCenterById = masterData.costCenters.find((c: any) => c.id === costCenterValue);
    if (costCenterById) {
      return costCenterById.name;
    }

    // Then try to find by name
    const costCenterByName = masterData.costCenters.find((c: any) => c.name === costCenterValue);
    if (costCenterByName) {
      return costCenterByName.name;
    }

    // Fallback to original value
    return costCenterValue;
  };

  // Helper function to resolve type name from ID
  const resolveTypeName = (typeValue: string | undefined, masterData?: any) => {
    if (!typeValue || !masterData?.types) return typeValue || '';

    // First try to find by ID
    const typeById = masterData.types.find((t: any) => t.id === typeValue);
    if (typeById) return typeById.name;

    // Then try to find by name
    const typeByName = masterData.types.find((t: any) => t.name === typeValue);
    if (typeByName) return typeByName.name;

    // Fallback to original value
    return typeValue;
  };

  // Helper function to resolve category name from ID
  const resolveCategoryName = (categoryValue: string | undefined, masterData?: any) => {
    if (!categoryValue || !masterData?.categories) return categoryValue || '';

    // First try to find by ID
    const categoryById = masterData.categories.find((c: any) => c.id === categoryValue);
    if (categoryById) return categoryById.name;

    // Then try to find by name
    const categoryByName = masterData.categories.find((c: any) => c.name === categoryValue);
    if (categoryByName) return categoryByName.name;

    // Fallback to original value
    return categoryValue;
  };

  // Columns definition (plain array, not useMemo)
  // Check if user can approve/reject (admin or approver roles)
  const canApproveReject = userRoles.includes('admin') || userRoles.includes('approver');

  const columns: ColumnDef<Expense>[] = [
    {
      id: 'select',
      header: ({ table }) => {
        // Get currently visible rows on this page
        const currentPageRows = table.getRowModel().rows.map(r => r.original);
        const allSelected = currentPageRows.length > 0 && currentPageRows.every(row => selectedRowIds[row.id]);
        
        return (
          <input
            type="checkbox"
            checked={allSelected}
            onChange={() => {
              // Toggle selection for all visible rows on current page only
              const newSelected = { ...selectedRowIds };
              if (allSelected) {
                currentPageRows.forEach(row => { delete newSelected[row.id]; });
              } else {
                currentPageRows.forEach(row => { newSelected[row.id] = true; });
              }
              setSelectedRowIds(newSelected);
            }}
            aria-label="Select all on this page"
            title="Select all on this page"
          />
        );
      },
      cell: ({ row }: { row: Row<Expense> }) => (
        <input
          type="checkbox"
          checked={!!selectedRowIds[row.original.id]}
          onChange={() => toggleRowSelected(row.original.id)}
          aria-label="Select row"
          title="Select row"
        />
      ),
      size: 32,
    },
    {
      accessorKey: 'date',
      header: ({ column }) => (
        <button
          className="flex items-center gap-1 hover:text-primary transition-colors"
          onClick={column.getToggleSortingHandler()}
          aria-label="Sort by date"
          title="Sort by date"
          type="button"
        >
          <Calendar size={14} />
          Date
          {column.getIsSorted() === 'asc' ? (
            <ChevronUp size={14} />
          ) : column.getIsSorted() === 'desc' ? (
            <ChevronDown size={14} />
          ) : (
            <ChevronsUpDown size={14} />
          )}
        </button>
      ),
      cell: (ctx) => formatDisplayDate(ctx.getValue() as Date),
      filterFn: 'includesString',
      size: columnWidths?.date ? parseInt(columnWidths.date) : 120,
    },
    {
      accessorKey: 'type',
      header: ({ column }) => (
        <button
          className="flex items-center gap-1 hover:text-primary transition-colors"
          onClick={column.getToggleSortingHandler()}
          aria-label="Sort by type"
          title="Sort by type"
        >
          Type
          {column.getIsSorted() === 'asc' ? (
            <ChevronUp size={14} />
          ) : column.getIsSorted() === 'desc' ? (
            <ChevronDown size={14} />
          ) : (
            <ChevronsUpDown size={14} />
          )}
        </button>
      ),
      cell: (ctx) => resolveTypeName(ctx.getValue() as string, masterData),
      size: columnWidths?.type ? parseInt(columnWidths.type) : 100,
    },
    {
      accessorKey: 'unit',
      header: ({ column }) => (
        <button
          className="flex items-center gap-1 hover:text-primary transition-colors"
          onClick={column.getToggleSortingHandler()}
          title="Sort by unit"
        >
          Unit
          {column.getIsSorted() === 'asc' ? (
            <ChevronUp size={14} />
          ) : column.getIsSorted() === 'desc' ? (
            <ChevronDown size={14} />
          ) : (
            <ChevronsUpDown size={14} />
          )}
        </button>
      ),
      cell: (ctx) => resolveUnitName(ctx.getValue() as string, masterData),
      filterFn: 'includesString',
      size: columnWidths?.unit ? parseInt(columnWidths.unit) : 100,
    },
    {
      accessorKey: 'costCenter',
      header: ({ column }) => (
        <button
          className="flex items-center gap-1 hover:text-primary transition-colors"
          onClick={column.getToggleSortingHandler()}
          title="Sort by cost center"
        >
          Cost Center
          {column.getIsSorted() === 'asc' ? (
            <ChevronUp size={14} />
          ) : column.getIsSorted() === 'desc' ? (
            <ChevronDown size={14} />
          ) : (
            <ChevronsUpDown size={14} />
          )}
        </button>
      ),
      cell: (ctx) => resolveCostCenterName(ctx.getValue() as string, masterData),
      filterFn: 'includesString',
      size: columnWidths?.costCenter ? parseInt(columnWidths.costCenter) : 120,
    },
    {
      accessorKey: 'category',
      header: ({ column }) => (
        <button
          className="flex items-center gap-1 hover:text-primary transition-colors"
          onClick={column.getToggleSortingHandler()}
          title="Sort by category"
        >
          Category
          {column.getIsSorted() === 'asc' ? (
            <ChevronUp size={14} />
          ) : column.getIsSorted() === 'desc' ? (
            <ChevronDown size={14} />
          ) : (
            <ChevronsUpDown size={14} />
          )}
        </button>
      ),
      filterFn: 'includesString',
      cell: (ctx) => (
        <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(resolveCategoryName(String(ctx.getValue() || ''), masterData)) }} />
      ),
      size: columnWidths?.category ? parseInt(columnWidths.category) : 120,
    },
    {
      accessorKey: 'nature',
      header: ({ column }) => (
        <button
          className="flex items-center gap-1 hover:text-primary transition-colors"
          onClick={column.getToggleSortingHandler()}
          title="Sort by nature"
        >
          Nature
          {column.getIsSorted() === 'asc' ? (
            <ChevronUp size={14} />
          ) : column.getIsSorted() === 'desc' ? (
            <ChevronDown size={14} />
          ) : (
            <ChevronsUpDown size={14} />
          )}
        </button>
      ),
      filterFn: 'includesString',
      cell: (ctx) => {
        const nature = String(ctx.getValue() || 'routine');
        const natureLabels = {
          'routine': 'Routine',
          'stock': 'Stock',
          'payroll': 'Payroll',
          'capital': 'Capital',
          'admin-finance': 'Admin/Finance',
          'admin': 'Admin'
        };
        const colors = {
          'routine': 'bg-info-100 text-info-800',
          'stock': 'bg-success-100 text-success-800',
          'payroll': 'bg-secondary-100 text-purple-800',
          'capital': 'bg-warning-100 text-orange-800',
          'admin-finance': 'bg-bg-subtle text-text-primary',
          'admin': 'bg-bg-subtle text-text-primary'
        };
        return (
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[nature as keyof typeof colors] || colors.routine}`}>
            {natureLabels[nature as keyof typeof natureLabels] || nature}
          </span>
        );
      },
      size: columnWidths?.nature ? parseInt(columnWidths.nature) : 100,
    },
    {
      accessorKey: 'shop',
      header: ({ column }) => (
        <button
          className="flex items-center gap-1 hover:text-primary transition-colors"
          onClick={column.getToggleSortingHandler()}
          title="Sort by shop"
        >
          Shop
          {column.getIsSorted() === 'asc' ? (
            <ChevronUp size={14} />
          ) : column.getIsSorted() === 'desc' ? (
            <ChevronDown size={14} />
          ) : (
            <ChevronsUpDown size={14} />
          )}
        </button>
      ),
      filterFn: 'includesString',
      cell: (ctx) => (
        <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(String(ctx.getValue() || '')) }} />
      ),
      size: columnWidths?.shop ? parseInt(columnWidths.shop) : 180,
    },
    {
      accessorKey: 'amount',
      header: ({ column }) => (
        <button
          className="flex items-center gap-1 hover:text-primary transition-colors text-right w-full justify-end"
          onClick={column.getToggleSortingHandler()}
        >
          Amount
          {column.getIsSorted() === 'asc' ? (
            <ChevronUp size={14} />
          ) : column.getIsSorted() === 'desc' ? (
            <ChevronDown size={14} />
          ) : (
            <ChevronsUpDown size={14} />
          )}
        </button>
      ),
      cell: (ctx) => `₹${Number(ctx.getValue()).toLocaleString('en-IN')}`,
      meta: { align: 'right' },
      size: columnWidths?.amount ? parseInt(columnWidths.amount) : 100,
    },
    {
      id: 'expand',
      header: '',
      cell: ({ row }) => (
        <button
          onClick={() => toggleRowExpansion(row.id)}
          className="p-1 rounded hover:bg-bg-subtle text-text-muted hover:text-text-secondary"
          title={expandedRows[row.id] ? 'Collapse details' : 'Expand details'}
        >
          {expandedRows[row.id] ? (
            <ChevronDown size={16} />
          ) : (
            <ChevronRight size={16} />
          )}
        </button>
      ),
      size: 40,
    },
    {
      accessorKey: 'approvalStatus',
      header: ({ column }) => (
        <button
          className="flex items-center gap-1 hover:text-primary transition-colors"
          onClick={column.getToggleSortingHandler()}
          title="Sort by status"
        >
          Status
          {column.getIsSorted() === 'asc' ? (
            <ChevronUp size={14} />
          ) : column.getIsSorted() === 'desc' ? (
            <ChevronDown size={14} />
          ) : (
            <ChevronsUpDown size={14} />
          )}
        </button>
      ),
      cell: (ctx) => {
        const status = ctx.getValue() as string;
        const statusColors = {
          pending: 'bg-warning-500 text-text-inverse',
          approved: 'bg-success-500 text-text-inverse',
          rejected: 'bg-error-500 text-text-inverse',
        };
        const statusColor = statusColors[status as keyof typeof statusColors] || 'bg-gray-500 text-text-inverse';
        return (
          <span className={`inline-block rounded-full px-2 py-0.5 text-xs whitespace-nowrap ${statusColor}`}>{status}</span>
        );
      },
      filterFn: 'includesString',
      size: columnWidths?.approvalStatus ? parseInt(columnWidths.approvalStatus) : 120,
      minSize: 120, // Ensure minimum width to prevent wrapping
    },
    {
      accessorKey: 'createdBy',
      header: ({ column }) => (
        <button
          className="flex items-center gap-1 hover:text-primary transition-colors"
          onClick={column.getToggleSortingHandler()}
          title="Sort by created by"
        >
          Created By
          {column.getIsSorted() === 'asc' ? (
            <ChevronUp size={14} />
          ) : column.getIsSorted() === 'desc' ? (
            <ChevronDown size={14} />
          ) : (
            <ChevronsUpDown size={14} />
          )}
        </button>
      ),
      cell: ({ row }) => (
        <AuditTooltip
          user={row.original.createdBy}
          timestamp={row.original.createdAt}
          label={shortEmail(row.original.createdBy || '')}
        />
      ),
      size: columnWidths?.createdBy ? parseInt(columnWidths.createdBy) : 180,
    },
    {
      accessorKey: 'createdAt',
      header: ({ column }) => (
        <button
          className="flex items-center gap-1 hover:text-primary transition-colors"
          onClick={column.getToggleSortingHandler()}
          title="Sort by created date"
        >
          Created At
          {column.getIsSorted() === 'asc' ? (
            <ChevronUp size={14} />
          ) : column.getIsSorted() === 'desc' ? (
            <ChevronDown size={14} />
          ) : (
            <ChevronsUpDown size={14} />
          )}
        </button>
      ),
      cell: ({ row }) => {
        const createdAt = row.original.createdAt;
        const createdBy = row.original.createdBy;
        if (!createdAt) {
          return <span className="text-text-placeholder text-xs">Unknown</span>;
        }
        return (
          <AuditTooltip
            user={createdBy || 'Unknown'}
            timestamp={createdAt}
            label={formatDisplayDate(createdAt)}
          />
        );
      },
      size: columnWidths?.createdAt ? parseInt(columnWidths.createdAt) : 180,
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        const expense = row.original;
        const canEditThis = canEdit(expense);
        const canDeleteThis = canDelete(expense);

        return (
          <div className="flex gap-1 flex-nowrap min-w-max">
            {canEditThis && (
              <button
                onClick={() => onEdit?.(expense)}
                className="p-1 rounded hover:bg-bg-subtle flex-shrink-0"
                aria-label="Edit"
                title="Edit"
                type="button"
              >
                <Pencil size={16} />
              </button>
            )}
            {canDeleteThis && (
              <button
                onClick={() => onDelete?.(expense)}
                className="p-1 rounded hover:bg-bg-subtle text-error-600 flex-shrink-0"
                aria-label="Delete"
                title="Delete"
                type="button"
              >
                <Trash size={16} />
              </button>
            )}
            {canApproveReject && (
              <div className="flex gap-1 flex-shrink-0">
                <QuickActionButtons
                  onApprove={expense.approvalStatus !== 'approved' && onApprove ? () => onApprove(expense) : undefined}
                  onReject={expense.approvalStatus !== 'rejected' && onReject ? () => onReject(expense) : undefined}
                  onDuplicate={onDuplicate ? () => onDuplicate(expense) : undefined}
                  loading={rowLoading?.[expense.id]}
                />
              </div>
            )}
          </div>
        );
      },
      size: columnWidths?.actions ? parseInt(columnWidths.actions) : 120,
    },
  ];
  // Table instance
  const table = useReactTable({
    data: advancedFilteredData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onPaginationChange: setPagination,
    state: {
      sorting,
      columnFilters,
      pagination,
    },
  });

  const exportableFields = [
    { key: 'date', label: 'Date' },
    { key: 'type', label: 'Type' },
    { key: 'unit', label: 'Unit' },
    { key: 'costCenter', label: 'Cost Center' },
    { key: 'category', label: 'Category' },
    { key: 'nature', label: 'Nature' },
    { key: 'shop', label: 'Shop' },
    { key: 'amount', label: 'Amount' },
    { key: 'description', label: 'Description' },
    { key: 'name', label: 'Name' },
    { key: 'approvalStatus', label: 'Status' },
    { key: 'createdBy', label: 'Created By' },
    { key: 'createdAt', label: 'Created Date' },
    { key: 'updatedBy', label: 'Updated By' },
    { key: 'updatedAt', label: 'Updated Date' },
  ];

  const handleExport = () => {
    const filteredData = advancedFilteredData;
    onExport?.(filteredData, selectedExportFields);
    setShowExportModal(false);
  };

  const getPreviewData = () => {
    const filteredData = advancedFilteredData.slice(0, 3);
    return filteredData;
  };

  // Bulk approve handler - now uses parent's bulk handler if provided
  const handleBulkApprove = () => {
    // Only approve expenses that aren't already approved
    const rowsToApprove = selectedRows.filter(row => row.approvalStatus !== 'approved');
    
    if (rowsToApprove.length === 0) return;
    
    if (onBulkApprove) {
      // Use the parent's bulk handler (better - single toast)
      onBulkApprove(rowsToApprove);
    } else if (onApprove) {
      // Fallback to individual calls (legacy - multiple toasts)
      rowsToApprove.forEach(row => {
        if (canEdit(row)) onApprove(row);
      });
    }
    
    setSelectedRowIds({});
  };

  // Bulk reject handler
  const handleBulkReject = () => {
    // Only reject expenses that aren't already rejected
    const rowsToReject = selectedRows.filter(row => row.approvalStatus !== 'rejected');
    
    if (rowsToReject.length === 0) return;
    
    if (onBulkReject) {
      // Use the parent's bulk handler (better - single toast)
      onBulkReject(rowsToReject);
    } else if (onReject) {
      // Fallback to individual calls (legacy - multiple toasts)
      rowsToReject.forEach(row => {
        if (canEdit(row)) onReject(row);
      });
    }
    
    setSelectedRowIds({});
  };

  return (
    <div className="space-y-4">
      {/* Header Controls */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
        <div className="flex flex-wrap gap-2">
          {/* Advanced Filters Toggle */}
          <button
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-colors ${showAdvancedFilters
                ? 'bg-info-100 text-info-700'
                : 'bg-bg-subtle text-text-secondary hover:bg-bg-disabled'
              }`}
          >
            <Filter size={16} />
            Advanced Filters
            {isFilterActive({
              dateRange: showAdvancedFilters ? {
                start: advancedFilters.dateFrom,
                end: advancedFilters.dateTo
              } : {
                start: getDefaultFilters().dateFrom,
                end: getDefaultFilters().dateTo
              },
              type: showAdvancedFilters ? advancedFilters.type : '',
              approvalStatus: showAdvancedFilters ? advancedFilters.approvalStatus as any : getDefaultFilters().approvalStatus as any
            }) && (
                <span className="bg-info-500 text-text-inverse text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  !
                </span>
              )}
          </button>

          {/* Quick Filter Presets - Always Visible */}
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-sm font-medium text-text-secondary">Quick Filters:</span>
            
            {/* Role-based default filter indicator */}
            {(() => {
              const defaults = getDefaultFilters();
              if (defaults.approvalStatus === 'pending') {
                return (
                  <span className="px-3 py-1 text-xs bg-warning-100 text-yellow-700 rounded-full border border-yellow-200">
                    Showing: Pending Items
                  </span>
                );
              } else if (defaults.approvalStatus === 'approved') {
                return (
                  <span className="px-3 py-1 text-xs bg-success-100 text-success-700 rounded-full border border-success-200">
                    Showing: Approved Items
                  </span>
                );
              }
              return (
                <span className="px-3 py-1 text-xs bg-info-100 text-info-700 rounded-full border border-info-200">
                  Showing: Last 30 Days
                </span>
              );
            })()}
            
            {/* Quick preset buttons */}
            <button
              onClick={() => {
                table.getColumn('approvalStatus')?.setFilterValue('pending');
                setAdvancedFilters(prev => ({ ...prev, approvalStatus: 'pending' }));
              }}
              className="px-3 py-1 text-xs bg-warning-100 text-yellow-700 rounded-full hover:bg-yellow-200 transition-colors border border-yellow-200"
              title="Show only pending items"
            >
              Pending Only
            </button>
            <button
              onClick={() => {
                table.getColumn('approvalStatus')?.setFilterValue('approved');
                setAdvancedFilters(prev => ({ ...prev, approvalStatus: 'approved' }));
              }}
              className="px-3 py-1 text-xs bg-success-100 text-success-700 rounded-full hover:bg-green-200 transition-colors border border-success-200"
              title="Show only approved items"
            >
              Approved Only
            </button>
            <button
              onClick={() => {
                table.getColumn('approvalStatus')?.setFilterValue('');
                const defaults = getDefaultFilters();
                setAdvancedFilters(prev => ({ ...prev, approvalStatus: defaults.approvalStatus }));
              }}
              className="px-3 py-1 text-xs bg-bg-subtle text-text-secondary rounded-full hover:bg-bg-disabled transition-colors border border-border-default"
              title="Reset to role-based default"
            >
              Reset Status
            </button>
          </div>

          {/* Quick Filters */}
          <input
            placeholder="Search all fields..."
            value={(table.getState().globalFilter as string) ?? ''}
            onChange={(e) => table.setGlobalFilter(e.target.value)}
            className="px-3 py-2 border border-border-muted rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/60"
          />

          {/* Column Filters */}
          <select
            value={(table.getColumn('type')?.getFilterValue() as string) ?? ''}
            onChange={(e) => table.getColumn('type')?.setFilterValue(e.target.value)}
            className="px-3 py-2 border border-border-muted rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/60"
          >
            <option value="">All Types</option>
            {filterOptions.types.map(type => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>

          <select
            value={(table.getColumn('unit')?.getFilterValue() as string) ?? ''}
            onChange={(e) => table.getColumn('unit')?.setFilterValue(e.target.value)}
            className="px-3 py-2 border border-border-muted rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/60"
          >
            <option value="">All Units</option>
            {filterOptions.units.map(unit => (
              <option key={unit.value} value={unit.value}>{unit.label}</option>
            ))}
          </select>
        </div>

        <div className="flex gap-2">
          <Button
            onClick={() => setShowExportModal(true)}
            className="flex items-center gap-2 text-sm"
            title="Export"
          >
            <Download size={16} />
            Export
          </Button>
          {canApproveReject && (
            <>
              <button
                onClick={handleBulkApprove}
                className="flex items-center gap-2 px-4 py-2 bg-ds-status-success-main hover:bg-ds-status-success-dark text-text-inverse rounded-lg transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={approvableCount === 0}
                title={approvableCount > 0 ? `Approve ${approvableCount} Selected` : 'No approvable expenses selected'}
              >
                Approve ({approvableCount})
              </button>
              <button
                onClick={handleBulkReject}
                className="flex items-center gap-2 px-4 py-2 bg-ds-status-error-main hover:bg-ds-status-error-dark text-text-inverse rounded-lg transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={rejectableCount === 0}
                title={rejectableCount > 0 ? `Reject ${rejectableCount} Selected` : 'No rejectable expenses selected'}
              >
                Reject ({rejectableCount})
              </button>
            </>
          )}
        </div>
      </div>

      {/* Advanced Filters Panel */}
      {showAdvancedFilters && (
        <div className="mt-4 p-4 bg-bg-muted rounded-lg border">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">Type</label>
              <select
                value={advancedFilters.type}
                onChange={(e) => setAdvancedFilters(prev => ({ ...prev, type: e.target.value }))}
                className="w-full px-3 py-2 border border-border-muted rounded-md focus:outline-none focus:ring-2 focus:ring-info-500 focus:border-info-500"
              >
                <option value="">All Types</option>
                {filterOptions.types.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">Unit</label>
              <select
                value={advancedFilters.unit}
                onChange={(e) => setAdvancedFilters(prev => ({ ...prev, unit: e.target.value }))}
                className="w-full px-3 py-2 border border-border-muted rounded-md focus:outline-none focus:ring-2 focus:ring-info-500 focus:border-info-500"
              >
                <option value="">All Units</option>
                {filterOptions.units.map(unit => (
                  <option key={unit.value} value={unit.value}>{unit.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">Nature</label>
              <select
                value={advancedFilters.nature || ''}
                onChange={(e) => setAdvancedFilters(prev => ({ ...prev, nature: e.target.value }))}
                className="w-full px-3 py-2 border border-border-muted rounded-md focus:outline-none focus:ring-2 focus:ring-info-500 focus:border-info-500"
              >
                <option value="">All Natures</option>
                <option value="routine">Routine</option>
                <option value="stock">Stock</option>
                <option value="payroll">Payroll</option>
                <option value="capital">Capital</option>
                <option value="admin-finance">Admin/Finance</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">Status</label>
              <select
                value={advancedFilters.approvalStatus}
                onChange={(e) => setAdvancedFilters(prev => ({ ...prev, approvalStatus: e.target.value }))}
                className="w-full px-3 py-2 border border-border-muted rounded-md focus:outline-none focus:ring-2 focus:ring-info-500 focus:border-info-500"
              >
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">Date From</label>
              <input
                type="date"
                value={advancedFilters.dateFrom}
                onChange={(e) => setAdvancedFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
                className="w-full px-3 py-2 border border-border-muted rounded-md focus:outline-none focus:ring-2 focus:ring-info-500 focus:border-info-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">Date To</label>
              <input
                type="date"
                value={advancedFilters.dateTo}
                onChange={(e) => setAdvancedFilters(prev => ({ ...prev, dateTo: e.target.value }))}
                className="w-full px-3 py-2 border border-border-muted rounded-md focus:outline-none focus:ring-2 focus:ring-info-500 focus:border-info-500"
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mt-4">
            <span className="text-sm font-medium text-text-secondary">Filter presets:</span>
            <button
              onClick={() => setAdvancedFilters({
                type: '',
                unit: '',
                approvalStatus: 'pending',
                dateFrom: '',
                dateTo: '',
                nature: ''
              })}
              className="px-3 py-1 text-xs bg-warning-100 text-yellow-700 rounded-full hover:bg-yellow-200 transition-colors"
            >
              Pending Items
            </button>
            <button
              onClick={() => setAdvancedFilters({
                type: '',
                unit: '',
                approvalStatus: 'approved',
                dateFrom: '',
                dateTo: '',
                nature: ''
              })}
              className="px-3 py-1 text-xs bg-success-100 text-success-700 rounded-full hover:bg-green-200 transition-colors"
            >
              Approved Items
            </button>
            <button
              onClick={() => {
                const today = new Date();
                const thisMonth = new Date(today.getFullYear(), today.getMonth(), 1);
                setAdvancedFilters({
                  type: '',
                  unit: '',
                  approvalStatus: '',
                  dateFrom: thisMonth.toISOString().split('T')[0],
                  dateTo: today.toISOString().split('T')[0],
                  nature: ''
                });
              }}
              className="px-3 py-1 text-xs bg-info-100 text-info-700 rounded-full hover:bg-blue-200 transition-colors"
            >
              This Month
            </button>
            <button
              onClick={() => {
                const today = new Date();
                const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
                setAdvancedFilters({
                  type: '',
                  unit: '',
                  approvalStatus: '',
                  dateFrom: lastMonth.toISOString().split('T')[0],
                  dateTo: lastMonthEnd.toISOString().split('T')[0],
                  nature: ''
                });
              }}
              className="px-3 py-1 text-xs bg-secondary-100 text-purple-700 rounded-full hover:bg-purple-200 transition-colors"
            >
              Last Month
            </button>
          </div>

          <div className="flex flex-wrap gap-2 mt-2">
            <span className="text-sm font-medium text-text-secondary">Date ranges:</span>
            {Object.entries(DATE_RANGE_PRESETS).map(([key, preset]) => (
              <button
                key={key}
                onClick={() => {
                  const dateRange = DATE_RANGE_PRESETS[key as keyof typeof DATE_RANGE_PRESETS].getValue();
                  setAdvancedFilters(prev => ({
                    ...prev,
                    dateFrom: dateRange.start,
                    dateTo: dateRange.end
                  }));
                }}
                className="px-3 py-1 text-xs bg-info-100 text-info-700 rounded-full hover:bg-blue-200 transition-colors"
              >
                {preset.label}
              </button>
            ))}
          </div>

          <div className="flex justify-end mt-4">
            <div className="flex gap-2">
              <button
                onClick={() => {
                  // Save current filter settings as default preferences
                  saveDefaultFilterPreferences({
                    dateRange: {
                      start: advancedFilters.dateFrom,
                      end: advancedFilters.dateTo
                    },
                    approvalStatus: advancedFilters.approvalStatus,
                    type: advancedFilters.type,
                    unit: advancedFilters.unit
                  });
                  // Show success message (you might want to add a toast notification here)
                  toast.success('Filter preferences saved as default!');
                }}
                className="flex items-center gap-1 px-3 py-1 text-sm text-success-600 hover:text-success-800 border border-green-300 rounded-md hover:bg-success-50 transition-colors"
                title="Save current filters as your default"
              >
                Save as Default
              </button>
              <button
                onClick={() => setAdvancedFilters(getDefaultFilters())}
                className="flex items-center gap-1 px-3 py-1 text-sm text-info-600 hover:text-info-800 border border-info-300 rounded-md hover:bg-info-50 transition-colors"
              >
                Reset to Defaults
              </button>
              <button
                onClick={() => setAdvancedFilters({
                  type: '',
                  unit: '',
                  approvalStatus: '',
                  dateFrom: '',
                  dateTo: '',
                  nature: ''
                })}
                className="flex items-center gap-1 px-3 py-1 text-sm text-text-secondary hover:text-text-primary border border-border-muted rounded-md hover:bg-bg-muted transition-colors"
              >
                <X size={14} />
                Clear Filters
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Data Table */}
      <div className="hidden md:block w-full">
        <div className={`bg-bg-surface rounded-lg border border-border-default ${containerClassName} ${stickyPagination ? 'mb-32' : 'mb-4'} w-full`}>
          <table className={tableClassName || "min-w-full table-fixed"} style={{ tableLayout: 'fixed' }}>
            <thead className="bg-bg-muted border-b border-border-default">
              {table.getHeaderGroups().map((headerGroup: HeaderGroup<Expense>) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header: Header<Expense, unknown>) => {
                    const width = header.column.columnDef.size || 120;
                    const colId = header.column.id;
                    const customClass = colId && columnClassNames[colId] ? columnClassNames[colId] : '';
                    return (
                      <th
                        key={header.id}
                        className={`px-4 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider ${customClass}`}
                        title={typeof header.column.columnDef.header === 'string' ? header.column.columnDef.header : undefined}
                        style={{ width, minWidth: width, maxWidth: width }}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
            <tbody className="bg-bg-surface divide-y divide-border-default">
              {loading ? (
                <tr>
                  <td colSpan={columns.length} className="px-4 py-8 text-center text-text-muted">
                    <div className="flex items-center justify-center space-x-2">
                      <div className="animate-spin h-5 w-5 border-2 border-primary border-t-transparent rounded-full"></div>
                      <span>Loading...</span>
                    </div>
                  </td>
                </tr>
              ) : table.getRowModel().rows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="px-4 py-8 text-center text-text-muted">
                    No data available
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map(row => (
                  <React.Fragment key={row.id}>
                    <tr className="hover:bg-bg-muted transition-colors">
                      {row.getVisibleCells().map(cell => {
                        const width = cell.column.columnDef.size || 120;
                        const colId = cell.column.id;
                        const customClass = colId && columnClassNames[colId] ? columnClassNames[colId] : '';
                        return (
                          <td
                            key={cell.id}
                            className={`px-4 py-3 text-sm text-text-primary ${(cell.column.columnDef as any).meta?.align === 'right' ? 'text-right' : ''} ${customClass}`}
                            style={{ wordBreak: 'break-word', width, minWidth: width, maxWidth: width }}
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        );
                      })}
                    </tr>
                    {expandedRows[row.id] && (
                      <tr className="bg-bg-muted border-t border-gray-100">
                        <td colSpan={columns.length} className="px-4 py-3">
                          <div className="text-sm">
                            <div className="font-medium text-text-secondary mb-2">Description:</div>
                            <div className="text-text-primary leading-relaxed">
                              <span dangerouslySetInnerHTML={{ 
                                __html: DOMPurify.sanitize(String(row.original.description || 'No description available')) 
                              }} />
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
        {/* Pagination - Always visible at bottom */}
        <div className={stickyPagination ? "bg-bg-surface px-4 py-3 border-t border-border-default sm:px-6 fixed bottom-0 z-40 shadow-[0_-4px_16px_-4px_rgba(0,0,0,0.1)] backdrop-blur-sm" : "bg-bg-surface px-4 py-3 border-t border-border-default sm:px-6"} 
             style={stickyPagination ? { left: '280px', right: '16px' } : undefined} 
             id={stickyPagination ? "sticky-pagination" : undefined}>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="text-sm text-text-secondary">
              Showing {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1} to{' '}
              {Math.min(
                (table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize,
                advancedFilteredData.length
              )}{' '}
              of {advancedFilteredData.length} results
              <span className="ml-2 text-xs text-info-600">
                (Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()})
              </span>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => table.setPageIndex(0)}
                disabled={!table.getCanPreviousPage()}
                className="p-2 rounded-lg border border-border-muted disabled:opacity-50 disabled:cursor-not-allowed hover:bg-bg-muted"
              >
                <ChevronsLeft size={16} />
              </button>
              <button
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
                className="p-2 rounded-lg border border-border-muted disabled:opacity-50 disabled:cursor-not-allowed hover:bg-bg-muted"
              >
                <ChevronLeft size={16} />
              </button>

              <span className="flex items-center gap-1 text-sm">
                Page
                <input
                  type="number"
                  min="1"
                  max={table.getPageCount()}
                  value={table.getState().pagination.pageIndex + 1}
                  onChange={(e) => {
                    const page = e.target.value ? Number(e.target.value) - 1 : 0;
                    table.setPageIndex(page);
                  }}
                  className="w-12 px-2 py-1 border border-border-muted rounded text-center"
                />
                of {table.getPageCount()}
              </span>

              <button
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
                className="p-2 rounded-lg border border-border-muted disabled:opacity-50 disabled:cursor-not-allowed hover:bg-bg-muted"
              >
                <ChevronRight size={16} />
              </button>
              <button
                onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                disabled={!table.getCanNextPage()}
                className="p-2 rounded-lg border border-border-muted disabled:opacity-50 disabled:cursor-not-allowed hover:bg-bg-muted"
              >
                <ChevronsRight size={16} />
              </button>

              <select
                value={table.getState().pagination.pageSize}
                onChange={(e) => table.setPageSize(Number(e.target.value))}
                className="px-3 py-1 border border-border-muted rounded-lg text-sm bg-bg-surface"
              >
                {[10, 25, 50, 100, 200].map(pageSize => (
                  <option key={pageSize} value={pageSize}>
                    {pageSize} per page
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="block md:hidden">
        {table.getRowModel().rows.map(row => (
          <ExpenseCard key={row.id} row={row.original} onEdit={onEdit} onDelete={onDelete} />
        ))}
      </div>

      {/* Export Modal */}
      <Modal
        open={showExportModal}
        onClose={() => setShowExportModal(false)}
        title="Export Data"
      >
        <div className="space-y-4">
          <div>
            <h4 className="font-medium mb-2">Select Fields to Export:</h4>
            <div className="grid grid-cols-2 gap-2">
              {exportableFields.map(field => (
                <label key={field.key} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={selectedExportFields.includes(field.key)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedExportFields(prev => [...prev, field.key]);
                      } else {
                        setSelectedExportFields(prev => prev.filter(f => f !== field.key));
                      }
                    }}
                    className="rounded border-border-muted text-primary focus:ring-primary"
                  />
                  <span className="text-sm">{field.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <h4 className="font-medium mb-2">Preview (First 3 rows):</h4>
            <div className="border border-border-default rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-bg-muted">
                    <tr>
                      {selectedExportFields.map(fieldKey => {
                        const field = exportableFields.find(f => f.key === fieldKey);
                        return (
                          <th key={fieldKey} className="px-3 py-2 text-left font-medium text-text-muted">
                            {field?.label}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border-default">
                    {getPreviewData().map((row, index) => (
                      <tr key={index}>
                        {selectedExportFields.map(fieldKey => (
                          <td key={fieldKey} className="px-3 py-2 text-text-primary">
                            {fieldKey === 'date' && row[fieldKey]
                              ? formatDisplayDate(row[fieldKey] as Date)
                              : fieldKey === 'amount'
                                ? `₹${Number(row[fieldKey] || 0).toLocaleString('en-IN')}`
                                : String(row[fieldKey as keyof Expense] || '')
                            }
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <button
              onClick={() => setShowExportModal(false)}
              className="px-4 py-2 border border-border-muted rounded-lg hover:bg-bg-muted transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleExport}
              disabled={selectedExportFields.length === 0}
              className="px-4 py-2 bg-primary text-text-inverse rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Download size={16} />
              Export {advancedFilteredData.length} records
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default EnterpriseDataTable;
